import { useQuery, useMutation } from '@tanstack/react-query';
import { queryClient } from '@/shared/services/queryClient';
import {
  customerGroupsApi,
} from '../services/customerGroupsApi';
import type {
  CreateCustomerGroupRequest,
  UpdateCustomerGroupRequest,
  CustomerGroupListQuery,
  CustomerGroupCriteria,
  CreateCustomerGroupFromCriteriaRequest,
} from '../services/customerGroupsApi';

export const useCustomerGroups = () => {
  return useQuery({
    queryKey: ['customerGroups'],
    queryFn: customerGroupsApi.getAllGroups,
  });
};

export const useCustomerGroupsPaged = (query: CustomerGroupListQuery) => {
  return useQuery({
    queryKey: ['customerGroups', 'paged', query],
    queryFn: () => customerGroupsApi.getGroupsPaged(query),
    placeholderData: (prev) => prev,
  });
};

export const useActiveCustomerGroups = () => {
  return useQuery({
    queryKey: ['customerGroups', 'active'],
    queryFn: customerGroupsApi.getActiveGroups,
  });
};

export const useCustomerGroupById = (id: string) => {
  return useQuery({
    queryKey: ['customerGroups', id],
    queryFn: () => customerGroupsApi.getGroupById(id),
  });
};

export const useCreateCustomerGroup = () => {
  return useMutation({
    mutationFn: (payload: CreateCustomerGroupRequest) =>
      customerGroupsApi.createGroup(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customerGroups'] });
      queryClient.invalidateQueries({
        queryKey: ['customerGroups', 'active'],
      });
    },
  });
};

export const useUpdateCustomerGroup = (id: string) => {
  return useMutation({
    mutationFn: (payload: UpdateCustomerGroupRequest) =>
      customerGroupsApi.updateGroup(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customerGroups'] });
      queryClient.invalidateQueries({
        queryKey: ['customerGroups', 'active'],
      });
      queryClient.invalidateQueries({
        queryKey: ['customerGroups', id],
      });
    },
  });
};

export const useDeleteCustomerGroup = () => {
  return useMutation({
    mutationFn: (id: string) => customerGroupsApi.deleteGroup(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customerGroups'] });
      queryClient.invalidateQueries({
        queryKey: ['customerGroups', 'active'],
      });
    },
  });
};

/**
 * Creates a customer group and bulk-assigns every criteria-matching customer.
 * Customer lists are invalidated too because CustomerGroupId tags change.
 */
export const useCreateCustomerGroupFromCriteria = () => {
  return useMutation({
    mutationFn: (payload: CreateCustomerGroupFromCriteriaRequest) =>
      customerGroupsApi.createGroupFromCriteria(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customerGroups'] });
      queryClient.invalidateQueries({ queryKey: ['customerGroups', 'active'] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
};

/** Bulk-assigns every criteria-matching customer into an existing group. */
export const useAssignCustomersByCriteria = () => {
  return useMutation({
    mutationFn: (payload: { groupId: string; criteria: CustomerGroupCriteria }) =>
      customerGroupsApi.assignCustomersByCriteria(payload.groupId, payload.criteria),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customerGroups'] });
      queryClient.invalidateQueries({ queryKey: ['customerGroups', 'active'] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
};